import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import {
  classifyPortalIdentityForEnforcement,
  planAdminEnforcementTrialWiring,
  type AdminEnforcementTrialEnvironment,
  type LegacyRouteReadinessState,
  previewPortalIdentityRouteEnforcementDryRun,
} from '@/lib/portalIdentityEnforcementPolicy';

function getAdminEnforcementTrialEnvironment(): AdminEnforcementTrialEnvironment {
  const mode = import.meta.env.MODE?.toLowerCase();

  if (mode === 'production') {
    return 'production';
  }

  if (mode === 'staging') {
    return 'staging';
  }

  if (import.meta.env.DEV) {
    return 'local';
  }

  return 'unknown';
}

function getLegacyAdminRouteReadiness(
  loading: boolean,
  user: unknown,
  isPlatformAdmin: boolean,
): LegacyRouteReadinessState {
  if (loading) {
    return 'legacy_loading';
  }

  if (!user || !isPlatformAdmin) {
    return 'legacy_redirects';
  }

  return 'legacy_allows';
}

export function SuperAdminProtectedRoute({ children }: { children: ReactNode }) {
  const {
    user,
    isPlatformAdmin,
    loading,
    portalIdentityDiagnostic,
    portalIdentityLoading,
  } = useAdminAuth();
  const portalIdentityPolicyPreview = classifyPortalIdentityForEnforcement(
    portalIdentityLoading ? null : portalIdentityDiagnostic,
  );
  const routeEnforcementDryRun = previewPortalIdentityRouteEnforcementDryRun(
    portalIdentityPolicyPreview,
  );
  void routeEnforcementDryRun;
  const adminEnforcementTrialWiring = planAdminEnforcementTrialWiring({
    trialFlagEnabled:
      import.meta.env.VITE_ENABLE_ADMIN_PORTAL_IDENTITY_ENFORCEMENT_TRIAL === 'true',
    environment: getAdminEnforcementTrialEnvironment(),
    legacyRouteReadiness: getLegacyAdminRouteReadiness(loading, user, isPlatformAdmin),
    diagnostic: portalIdentityLoading ? null : portalIdentityDiagnostic,
  });
  void adminEnforcementTrialWiring;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/auth" replace />;
  }

  if (!isPlatformAdmin) {
    return <Navigate to="/admin/auth" replace />;
  }

  return <>{children}</>;
}
